import {
  useQuery,
  useMutation,
  UseQueryOptions,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import apiClient from '../client';
import {
  MeetingAnnouncementApplicationView,
  MeetingAnnouncementApplicationInput,
  MeetingAnnouncementApplicationStatusUpdateInput,
  MeetingAnnouncementApplicationConfirmInput,
  UserRatingInput,
  UserRatingView,
  ApiError,
  ApiPlatformCollectionResponse,
  extractCollectionItems,
} from '../types';

export const meetingApplicationKeys = {
  all: ['meetingApplications'] as const,
  lists: () => [...meetingApplicationKeys.all, 'list'] as const,
  forAnnouncement: (announcementId: string) =>
    [
      ...meetingApplicationKeys.lists(),
      'announcement',
      announcementId,
    ] as const,
  mine: () => [...meetingApplicationKeys.lists(), 'mine'] as const,
  details: () => [...meetingApplicationKeys.all, 'detail'] as const,
  detail: (id: string) => [...meetingApplicationKeys.details(), id] as const,
};

export const useAnnouncementApplications = (
  announcementId: string,
  options?: Omit<
    UseQueryOptions<MeetingAnnouncementApplicationView[], ApiError>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery<MeetingAnnouncementApplicationView[], ApiError>({
    queryKey: meetingApplicationKeys.forAnnouncement(announcementId),
    queryFn: async () => {
      const response = await apiClient.get<
        ApiPlatformCollectionResponse<MeetingAnnouncementApplicationView>
      >(`/meeting-announcements/${announcementId}/applications`);
      return extractCollectionItems(response.data);
    },
    enabled: !!announcementId,
    ...options,
  });
};

export const useMyApplications = (
  options?: Omit<
    UseQueryOptions<MeetingAnnouncementApplicationView[], ApiError>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery<MeetingAnnouncementApplicationView[], ApiError>({
    queryKey: meetingApplicationKeys.mine(),
    queryFn: async () => {
      const response = await apiClient.get<
        ApiPlatformCollectionResponse<MeetingAnnouncementApplicationView>
      >('/meeting-applications/me');
      return extractCollectionItems(response.data);
    },
    ...options,
  });
};

export const useCreateApplication = (
  options?: UseMutationOptions<
    MeetingAnnouncementApplicationView,
    ApiError,
    { announcementId: string; data: MeetingAnnouncementApplicationInput }
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<
    MeetingAnnouncementApplicationView,
    ApiError,
    { announcementId: string; data: MeetingAnnouncementApplicationInput }
  >({
    mutationFn: async ({
      announcementId,
      data,
    }: {
      announcementId: string;
      data: MeetingAnnouncementApplicationInput;
    }) => {
      const response = await apiClient.post<MeetingAnnouncementApplicationView>(
        `/meeting-announcements/${announcementId}/applications`,
        data,
      );
      return response.data;
    },
    onSuccess: (
      data: MeetingAnnouncementApplicationView,
      variables: {
        announcementId: string;
        data: MeetingAnnouncementApplicationInput;
      },
    ) => {
      queryClient.invalidateQueries({
        queryKey: meetingApplicationKeys.forAnnouncement(
          variables.announcementId,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: meetingApplicationKeys.mine(),
      });
    },
    ...options,
  });
};

export const useUpdateApplicationStatus = (
  options?: UseMutationOptions<
    MeetingAnnouncementApplicationView,
    ApiError,
    {
      applicationId: string;
      data: MeetingAnnouncementApplicationStatusUpdateInput;
    }
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<
    MeetingAnnouncementApplicationView,
    ApiError,
    {
      applicationId: string;
      data: MeetingAnnouncementApplicationStatusUpdateInput;
    }
  >({
    mutationFn: async ({
      applicationId,
      data,
    }: {
      applicationId: string;
      data: MeetingAnnouncementApplicationStatusUpdateInput;
    }) => {
      const response =
        await apiClient.patch<MeetingAnnouncementApplicationView>(
          `/meeting-applications/${applicationId}/status`,
          data,
        );
      return response.data;
    },
    onSuccess: (
      data: MeetingAnnouncementApplicationView,
      variables: {
        applicationId: string;
        data: MeetingAnnouncementApplicationStatusUpdateInput;
      },
    ) => {
      queryClient.setQueryData(
        meetingApplicationKeys.detail(variables.applicationId),
        data,
      );
      queryClient.invalidateQueries({
        queryKey: meetingApplicationKeys.lists(),
      });
    },
    ...options,
  });
};

export const useConfirmMeetingCompletion = (
  options?: UseMutationOptions<
    MeetingAnnouncementApplicationView,
    ApiError,
    {
      applicationId: string;
      data: MeetingAnnouncementApplicationConfirmInput;
    }
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<
    MeetingAnnouncementApplicationView,
    ApiError,
    {
      applicationId: string;
      data: MeetingAnnouncementApplicationConfirmInput;
    }
  >({
    mutationFn: async ({
      applicationId,
      data,
    }: {
      applicationId: string;
      data: MeetingAnnouncementApplicationConfirmInput;
    }) => {
      const response = await apiClient.post<MeetingAnnouncementApplicationView>(
        `/meeting-applications/${applicationId}/confirm`,
        data,
      );
      return response.data;
    },
    onSuccess: (
      data: MeetingAnnouncementApplicationView,
      variables: {
        applicationId: string;
        data: MeetingAnnouncementApplicationConfirmInput;
      },
    ) => {
      queryClient.setQueryData(
        meetingApplicationKeys.detail(variables.applicationId),
        data,
      );
      queryClient.invalidateQueries({
        queryKey: meetingApplicationKeys.lists(),
      });
    },
    ...options,
  });
};

export const useRateMeetingParticipant = (
  options?: UseMutationOptions<
    UserRatingView,
    ApiError,
    {
      applicationId: string;
      data: UserRatingInput;
    }
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<
    UserRatingView,
    ApiError,
    {
      applicationId: string;
      data: UserRatingInput;
    }
  >({
    mutationFn: async ({
      applicationId,
      data,
    }: {
      applicationId: string;
      data: UserRatingInput;
    }) => {
      const response = await apiClient.post<UserRatingView>(
        `/meeting-applications/${applicationId}/ratings`,
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: meetingApplicationKeys.lists(),
      });
    },
    ...options,
  });
};

export const useRejectApplication = (
  options?: UseMutationOptions<
    MeetingAnnouncementApplicationView,
    ApiError,
    { applicationId: string }
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<
    MeetingAnnouncementApplicationView,
    ApiError,
    { applicationId: string }
  >({
    mutationFn: async ({ applicationId }: { applicationId: string }) => {
      const response =
        await apiClient.patch<MeetingAnnouncementApplicationView>(
          `/meeting-applications/${applicationId}/reject`,
        );
      return response.data;
    },
    onSuccess: (
      data: MeetingAnnouncementApplicationView,
      variables: { applicationId: string },
    ) => {
      queryClient.setQueryData(
        meetingApplicationKeys.detail(variables.applicationId),
        data,
      );
      queryClient.invalidateQueries({
        queryKey: meetingApplicationKeys.lists(),
      });
    },
    ...options,
  });
};
